import React from "react";
import { FaLinkedinIn, FaGithub, FaEnvelope } from "react-icons/fa";
import heroImage from "../assets/team.jpg";
import person1 from "../assets/person1_cropped.jpg";
import per2 from "../assets/per2.jpg";

export default function TeamPage() {
  const teamMembers = [
    {
      name: "Alishba Zahid",
      role: "CEO & Founder",
      image: person1,
      bio: "An expert in machine learning systems and intelligent algorithms, Alishba directs the artificial intelligence and analytics operations at Atronm. She has deep expertise in neural networks, natural language processing, and scalable backend platforms.",
      skills: ["AI & Machine Learning", "Project Management", "Python & PyTorch", "Backend Systems"],
      socials: {
        linkedin: "https://www.linkedin.com/in/alishba-zahid-4b960a334/",
        github: "https://github.com/alishbazahid42",
        email: "mailto:contact@atronm.com"
      }
    },
    {
      name: "Muhammad Moiz Ahmad",
      role: "Co-Founder & Manager",
      image: per2,
      bio: "A visionary full-stack engineer and tech strategist, Moiz leads the core product engineering and cloud architecture at Atronm. He specializes in crafting high-performance, secure, and user-centric web applications that scale seamlessly.",
      skills: ["Full-Stack Dev", "React & Node.js", "Cloud Architecture", "Product Strategy"],
      socials: {
        linkedin: "https://www.linkedin.com/in/moiz-ahmad-8922651a7/",
        github: "https://github.com/BSCE22029",
        email: "mailto:contact@atronm.com"
      }
    },
    {
      name: "Muhammad Arham",
      role: "Chief Technology Officer",
      image: per2,
      bio: "Leading our core technology division, Arham brings extensive experience in system design, security, and developer operations. He oversees technology integrations, infrastructure, and coding standards.",
      skills: ["System Design", "DevOps & CI/CD", "Software Security", "Engineering Lead"],
      socials: {
        linkedin: "https://www.linkedin.com/in/muhammad-arham-0012282a5/",
        github: "https://github.com/arhamtahir007",
        email: "mailto:contact@atronm.com"
      }
    },
    {
      name: "Mohsin Raza Aujla",
      role: "Chief Financial Officer",
      image: per2,
      bio: "Mohsin manages the financial operations, investment strategy, and compliance at Atronm. He ensures the financial health of the firm supports our continuous tech innovations and long-term scaling goals.",
      skills: ["Financial Planning", "Startup Growth", "Risk Analysis", "Operations Strategy"],
      socials: {
        linkedin: "https://www.linkedin.com/in/mohsin-raza-aujla-bsse23040/",
        github: "https://github.com/mohsinojla",
        email: "mailto:contact@atronm.com"
      }
    }
  ];

  return (
    <section className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-gray-800 font-sans">
      
      {/* Hero Header Banner */}
      <div
        className="relative bg-cover bg-center text-white py-16 sm:py-28 px-4 sm:px-6 text-center shadow-lg"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-[2px]"></div>
        <div className="relative z-10 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 tracking-tight drop-shadow-md">
            Meet Our Team
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-slate-200 max-w-2xl mx-auto drop-shadow-sm font-light">
            Bringing together engineering excellence, AI intelligence, and design innovation.
          </p>
        </div>
      </div>

      {/* Intro Philosophy Section */}
      <div className="max-w-6xl mx-auto py-16 px-6 text-center" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-slate-900 mb-4">Driven by Innovation, Defined by Quality</h2>
        <p className="text-gray-600 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
          At Atronm, we don't just write software; we architect solutions that elevate businesses. Our leadership blends advanced technical capability with goal-oriented strategy to build products that empower users and scale dynamically.
        </p>
      </div>

      {/* Team Cards Grid */}
      <div className="max-w-5xl mx-auto pb-24 px-6">
        <div className="grid md:grid-cols-2 gap-12">
          {teamMembers.map((member, idx) => (
            <div
              key={idx}
              className="bg-white border border-slate-100 rounded-3xl p-8 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col items-center group relative overflow-hidden"
              data-aos="fade-up"
              data-aos-delay={idx * 150}
            >
              {/* Background Accent Blob on Hover */}
              <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-blue-50 opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10"></div>
              
              {/* Profile Image with Ring Effect */}
              <div className="relative mb-6">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full blur opacity-25 group-hover:opacity-60 transition duration-300"></div>
                <img
                  src={member.image}
                  alt={member.name}
                  className="relative w-40 h-40 rounded-full object-cover border-4 border-white shadow-md transition-transform duration-500 group-hover:scale-105"
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                />
              </div>

              {/* Name & Title */}
              <h3 className="text-2xl font-extrabold text-slate-900 group-hover:text-blue-600 transition-colors duration-300">
                {member.name}
              </h3>
              <p className="text-sm font-semibold text-purple-600 uppercase tracking-wider mt-1 mb-4">
                {member.role}
              </p>

              {/* Bio Description */}
              <p className="text-gray-650 text-center text-sm leading-relaxed mb-6 font-light max-w-sm">
                {member.bio}
              </p>

              {/* Skill Tags */}
              <div className="flex flex-wrap gap-2 justify-center mb-8 max-w-xs">
                {member.skills.map((skill, sIdx) => (
                  <span
                    key={sIdx}
                    className="px-3 py-1 bg-slate-50 border border-slate-100 rounded-full text-xs text-slate-600 font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>

              {/* Social Links */}
              <div className="flex gap-4 mt-auto">
                <a
                  href={member.socials.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-55 hover:bg-blue-600 hover:text-white flex items-center justify-center border border-slate-200 text-slate-600 transition-all duration-300"
                  aria-label={`${member.name} LinkedIn`}
                >
                  <FaLinkedinIn />
                </a>
                <a
                  href={member.socials.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-55 hover:bg-slate-900 hover:text-white flex items-center justify-center border border-slate-200 text-slate-600 transition-all duration-300"
                  aria-label={`${member.name} GitHub`}
                >
                  <FaGithub />
                </a>
                <a
                  href={member.socials.email}
                  className="w-10 h-10 rounded-full bg-slate-55 hover:bg-purple-600 hover:text-white flex items-center justify-center border border-slate-200 text-slate-600 transition-all duration-300"
                  aria-label={`${member.name} Email`}
                >
                  <FaEnvelope />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Core Values Section */}
      <div className="bg-slate-900 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16" data-aos="fade-down">
            <h2 className="text-3xl font-extrabold tracking-tight">Our Work Culture & Values</h2>
            <p className="text-slate-400 mt-4 leading-relaxed">
              We align our design, development, and execution with a set of principles that ensure client success and engineering excellence.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
            <div className="p-6 bg-slate-800/50 border border-slate-700/40 rounded-2xl" data-aos="fade-up">
              <h3 className="text-lg font-bold mb-2 text-blue-400">Security-First Dev</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                We believe software is only as good as its security. Best practices in coding and dependency management are baked in from day one.
              </p>
            </div>
            
            <div className="p-6 bg-slate-800/50 border border-slate-700/40 rounded-2xl" data-aos="fade-up" data-aos-delay="100">
              <h3 className="text-lg font-bold mb-2 text-purple-400">Collaboration & Clarity</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Clear communication and structured sprint reviews ensure you always know where development stands and what gets delivered next.
              </p>
            </div>

            <div className="p-6 bg-slate-800/50 border border-slate-700/40 rounded-2xl" data-aos="fade-up" data-aos-delay="200">
              <h3 className="text-lg font-bold mb-2 text-green-400">Continuous Innovation</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                We actively build predictive intelligence, NLP systems, and high-speed web apps utilizing cutting-edge ML and web frameworks.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16 px-6 text-center">
        <div className="max-w-4xl mx-auto space-y-6" data-aos="zoom-in">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">Ready to Work with Us?</h2>
          <p className="text-blue-100 max-w-xl mx-auto text-base sm:text-lg font-light leading-relaxed">
            Let's translate your business objectives into robust, smart, and scale-ready technological solutions.
          </p>
          <div className="pt-2">
            <a
              href="/contact"
              className="inline-block bg-white hover:bg-blue-50 text-blue-600 font-bold px-8 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105"
            >
              Get In Touch
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
